/**
 * DocumentDraftPanel.tsx
 * 
 * Real-time document co-authoring panel with TipTap editor.
 * Displays as a modal overlay above the chat interface.
 * Allows user to edit sections, request AI updates, and export to PDF/DOCX.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Stack,
    Text,
    Icon,
    IconButton,
    PrimaryButton,
    DefaultButton,
    Spinner,
    SpinnerSize,
    TextField,
    MessageBar,
    MessageBarType,
    TooltipHost,
    Dropdown,
    IDropdownOption,
    Callout,
    DirectionalHint
} from '@fluentui/react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import { marked } from 'marked';
import { authenticatedFetch } from '../../utils/authApi';
import { deleteWorkspace as deleteWorkspaceApi } from '../../api/documentWorkspaceApi';
import { buildApiUrl } from '../../config/apiConfig';
import { openFile, downloadFile, isOfficeFile, isViewableFile } from '../../utils/fileOpenUtils';
import { DocumentWorkspaceSharingModal } from './DocumentWorkspaceSharingModal';
import styles from './DocumentDraftPanel.module.css';

// Configure marked for safe HTML output
marked.setOptions({
    breaks: true,  // Convert \n to <br>
    gfm: true      // GitHub Flavored Markdown
});

// Types
export interface DocumentSection {
    id: string;
    title: string;
    level: number;
    content: string;
    status: 'draft' | 'ai_updating' | 'user_editing' | 'approved';
    order: number;
    // Co-authoring tracking
    section_version?: number;
    last_edited_by?: string;
    last_edited_by_name?: string;
    updated_at?: string;
}

export interface DocumentDraft {
    id: string;
    user_id: string;
    conversation_id: string;
    title: string;
    format: 'pdf' | 'docx' | 'pptx';
    sections: DocumentSection[];
    status: 'drafting' | 'ready' | 'exported';
    version: number;
    // Export info
    exportedUrl?: string;
    // Sharing info
    sharedWithUsers?: string[];
    isPublic?: boolean;
    userId?: string;  // Owner
}

interface DocumentDraftPanelProps {
    draft: DocumentDraft | null;
    conversationId: string;
    workspaceId?: string;  // Load existing workspace by ID
    refreshKey?: number;   // Increment to trigger refresh (for updates from chat)
    isRtl?: boolean;
    sidePanel?: boolean;  // Show as side panel instead of modal overlay
    isCollapsed?: boolean;  // Panel is minimized to a thin strip
    isPinned?: boolean;  // Workspace is pinned to conversation
    hasUpdate?: boolean;  // Show update indicator on collapsed panel (content was added)
    onClose: () => void;
    onCollapse?: () => void;  // Minimize panel
    onExpand?: () => void;  // Expand from collapsed
    onClearUpdate?: () => void;  // Clear the update indicator when panel is expanded
    onPin?: () => void;  // Pin workspace to conversation
    onUnpin?: () => void;  // Unpin workspace
    onSectionUpdate?: (sectionId: string, content: string) => void;
    onAiRequest?: (sectionId: string, instruction: string) => void;
    onExport?: (format: 'pdf' | 'docx' | 'pptx') => void;
    onDraftChange?: (draft: DocumentDraft) => void;
    onDeleted?: () => void;  // Called after workspace is deleted
}

// API functions - using document workspaces API
const API_BASE = '/api/document-workspaces';

async function getWorkspaceById(workspaceId: string): Promise<DocumentDraft | null> {
    const res = await authenticatedFetch(`${API_BASE}/${workspaceId}`);
    const data = await res.json();
    return data.success ? data.workspace : null;
}

async function createDraft(
    conversationId: string,
    title: string,
    format: string
): Promise<DocumentDraft | null> {
    const res = await authenticatedFetch(`${API_BASE}/create`, {
        method: 'POST',
        body: JSON.stringify({ conversation_id: conversationId, title, format })
    });
    const data = await res.json();
    return data.success ? data.workspace : null;
}

async function getDraftByConversation(conversationId: string): Promise<DocumentDraft | null> {
    const res = await authenticatedFetch(`${API_BASE}/by-conversation/${conversationId}`);
    const data = await res.json();
    return data.workspace || null;
}

async function updateSection(
    draftId: string,
    sectionId: string,
    content: string
): Promise<DocumentSection | null> {
    const res = await authenticatedFetch(`${API_BASE}/${draftId}/sections/${sectionId}`, {
        method: 'PATCH',
        body: JSON.stringify({ content })
    });
    const data = await res.json();
    return data.success ? data.section : null;
}

async function addSection(
    draftId: string,
    title: string,
    content: string = '',
    afterSectionId?: string
): Promise<DocumentSection | null> {
    const res = await authenticatedFetch(`${API_BASE}/${draftId}/sections`, {
        method: 'POST',
        body: JSON.stringify({ title, content, after_section_id: afterSectionId })
    });
    const data = await res.json();
    return data.success ? data.section : null;
}

async function deleteSection(draftId: string, sectionId: string): Promise<boolean> {
    const res = await authenticatedFetch(`${API_BASE}/${draftId}/sections/${sectionId}`, {
        method: 'DELETE'
    });
    const data = await res.json();
    return data.success;
}

async function reorderSections(draftId: string, sectionOrder: string[]): Promise<boolean> {
    const res = await authenticatedFetch(`${API_BASE}/${draftId}/reorder`, {
        method: 'POST',
        body: JSON.stringify({ section_order: sectionOrder })
    });
    const data = await res.json();
    return data.success;
}

async function requestAiUpdate(
    draftId: string,
    sectionId: string,
    instruction: string,
    enableWebSearch: boolean = true
): Promise<DocumentSection | null> {
    // Use enhanced endpoint with web search capability
    const res = await authenticatedFetch(`${API_BASE}/${draftId}/ai-update-enhanced/${sectionId}`, {
        method: 'POST',
        body: JSON.stringify({ instruction, enable_web_search: enableWebSearch })
    });
    const data = await res.json();
    return data.success ? data.section : null;
}

async function requestFullDocumentUpdate(
    draftId: string,
    instruction: string,
    enableWebSearch: boolean = true
): Promise<DocumentDraft | null> {
    const res = await authenticatedFetch(`${API_BASE}/${draftId}/ai-update-document`, {
        method: 'POST',
        body: JSON.stringify({ instruction, enable_web_search: enableWebSearch })
    });
    const data = await res.json();
    return data.success ? data.workspace : null;
}

async function exportDraft(
    draftId: string,
    format: string,
    generateImages: boolean = true
): Promise<{ success: boolean; document_url?: string; filename?: string; error?: string; image_stats?: any }> {
    const res = await authenticatedFetch(`${API_BASE}/${draftId}/export`, {
        method: 'POST',
        body: JSON.stringify({ format, generate_images: generateImages })
    });
    const result = await res.json();
    // Build full URL for cross-origin support
    if (result.document_url && !result.document_url.startsWith('http')) {
        result.document_url = buildApiUrl(result.document_url);
    }
    return result;
}

async function deleteWorkspace(workspaceId: string): Promise<boolean> {
    // Use centralized API that handles cache invalidation
    return await deleteWorkspaceApi(workspaceId);
}

// Section Editor Component

// Helper to convert markdown to HTML for TipTap
const markdownToHtml = (markdown: string): string => {
    if (!markdown) return '';
    // Check if content is already HTML (starts with < tag)
    if (markdown.trim().startsWith('<')) {
        return markdown;
    }
    // Convert markdown to HTML
    const html = marked.parse(markdown);
    return typeof html === 'string' ? html : '';
};

// Detect RTL content based on Hebrew character ratio
const isRTLContent = (content: string): boolean => {
    if (!content) return false;
    const hebrewCount = (content.match(/[\u0590-\u05FF]/g) || []).length;
    const latinCount = (content.match(/[a-zA-Z]/g) || []).length;
    return hebrewCount > 10 && (hebrewCount > latinCount || hebrewCount / (hebrewCount + latinCount) > 0.2);
};

const SectionEditor: React.FC<{
    section: DocumentSection;
    isExpanded: boolean;
    onToggle: () => void;
    onContentChange: (content: string) => void;
    onAiRequest: (instruction: string) => void;
    onDelete: () => void;
    isRtl: boolean;
    // Drag and drop props
    onDragStart?: (e: React.DragEvent, sectionId: string) => void;
    onDragOver?: (e: React.DragEvent) => void;
    onDragEnd?: () => void;
    onDrop?: (e: React.DragEvent, targetSectionId: string) => void;
    isDragging?: boolean;
    isDragOver?: boolean;
}> = ({ section, isExpanded, onToggle, onContentChange, onAiRequest, onDelete, isRtl, 
       onDragStart, onDragOver, onDragEnd, onDrop, isDragging, isDragOver }) => {
    const [aiInstruction, setAiInstruction] = useState('');
    const [showAiInput, setShowAiInput] = useState(false);
    const [showImageHelper, setShowImageHelper] = useState(false);
    const [imageCopied, setImageCopied] = useState(false);
    const imageButtonRef = React.useRef<HTMLDivElement>(null);
    
    // Convert markdown content to HTML for the editor
    const htmlContent = markdownToHtml(section.content);
    
    // Detect RTL based on section content OR global RTL setting
    const sectionIsRtl = isRtl || isRTLContent(section.content);
    
    const editor = useEditor({
        extensions: [
            StarterKit,
            Placeholder.configure({ placeholder: sectionIsRtl ? 'התחל לכתוב...' : 'Start writing...' }),
            Typography
        ],
        content: htmlContent,
        editorProps: {
            attributes: {
                class: styles.editorContent,
                dir: sectionIsRtl ? 'rtl' : 'ltr'
            }
        },
        onUpdate: ({ editor }) => {
            onContentChange(editor.getHTML());
        }
    });

    // Update editor when section content changes externally
    useEffect(() => {
        if (editor) {
            const newHtml = markdownToHtml(section.content);
            // Only update if content actually changed (avoid cursor jump)
            if (newHtml !== editor.getHTML()) {
                editor.commands.setContent(newHtml);
            }
        }
    }, [section.content, editor]);

    const handleAiSubmit = () => {
        if (aiInstruction.trim()) {
            onAiRequest(aiInstruction);
            setAiInstruction('');
            setShowAiInput(false);
        }
    };

    const getStatusIndicator = () => {
        switch (section.status) {
            case 'ai_updating':
                return <Spinner size={SpinnerSize.xSmall} className={styles.statusSpinner} />;
            case 'approved':
                return <span className={styles.statusApproved}>✓</span>;
            default:
                return null;
        }
    };

    return (
        <div 
            className={`${styles.sectionContainer} ${isExpanded ? styles.expanded : ''} ${isDragging ? styles.dragging : ''} ${isDragOver ? styles.dragOver : ''}`}
            onDragOver={(e) => { e.preventDefault(); onDragOver?.(e); }}
            onDrop={(e) => onDrop?.(e, section.id)}
        >
            <div className={styles.sectionHeader} onClick={onToggle}>
                <div className={styles.sectionTitleRow}>
                    <TooltipHost content={isRtl ? "גרור לשינוי סדר" : "Drag to reorder"}>
                        <div
                            draggable={true}
                            onDragStart={(e) => onDragStart?.(e, section.id)}
                            onDragEnd={onDragEnd}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <IconButton
                                iconProps={{ iconName: 'GripperBarHorizontal' }}
                                className={styles.dragHandle}
                                styles={{
                                    root: { cursor: 'grab', minWidth: 24, height: 24 },
                                    rootHovered: { backgroundColor: '#e8e8e8' }
                                }}
                            />
                        </div>
                    </TooltipHost>
                    <IconButton
                        iconProps={{ iconName: isExpanded ? 'ChevronDown' : 'ChevronRight' }}
                        className={styles.expandIcon}
                    />
                    <Text className={styles.sectionTitle}>
                        {section.title}
                    </Text>
                    {getStatusIndicator()}
                    {/* Edited by tag */}
                    {section.last_edited_by_name && (
                        <TooltipHost content={`Last edited ${section.updated_at ? new Date(section.updated_at).toLocaleString() : ''}`}>
                            <span style={{
                                fontSize: '11px',
                                color: '#666',
                                backgroundColor: '#f0f0f0',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                marginLeft: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                <span style={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    backgroundColor: '#0078d4',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '9px',
                                    fontWeight: 600
                                }}>
                                    {section.last_edited_by_name.charAt(0).toUpperCase()}
                                </span>
                                {section.last_edited_by_name}
                            </span>
                        </TooltipHost>
                    )}
                </div>
                <div className={styles.sectionActions}>
                    <TooltipHost content={isRtl ? "עדכון AI" : "AI Update"}>
                        <IconButton
                            iconProps={{ iconName: 'Robot' }}
                            onClick={(e) => { e.stopPropagation(); setShowAiInput(!showAiInput); }}
                            disabled={section.status === 'ai_updating'}
                        />
                    </TooltipHost>
                    <div ref={imageButtonRef}>
                        <TooltipHost content={isRtl ? "הוסף תמונה" : "Add Image"}>
                            <IconButton
                                iconProps={{ iconName: 'Photo2' }}
                                onClick={(e) => { e.stopPropagation(); setShowImageHelper(!showImageHelper); }}
                            />
                        </TooltipHost>
                    </div>
                    {showImageHelper && imageButtonRef.current && (
                        <Callout
                            target={imageButtonRef.current}
                            onDismiss={() => { setShowImageHelper(false); setImageCopied(false); }}
                            directionalHint={isRtl ? DirectionalHint.leftCenter : DirectionalHint.rightCenter}
                            styles={{ root: { maxWidth: 320, padding: 16 } }}
                        >
                            <div style={{ direction: isRtl ? 'rtl' : 'ltr' }}>
                                <Text variant="mediumPlus" style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
                                    {isRtl ? '🖼️ הוספת תמונה לשקף' : '🖼️ Add Image to Slide'}
                                </Text>
                                <Text variant="small" style={{ display: 'block', marginBottom: 12, color: '#555' }}>
                                    {isRtl 
                                        ? 'הוסף תג תמונה לתוכן ובעת ייצוא המצגת, תמונה תיווצר אוטומטית על ידי AI.'
                                        : 'Add an image tag to your content. When exporting the presentation, AI will generate the image automatically.'}
                                </Text>
                                <div style={{ 
                                    backgroundColor: '#f5f5f5', 
                                    padding: '10px 12px', 
                                    borderRadius: 6, 
                                    fontFamily: 'monospace',
                                    fontSize: 13,
                                    marginBottom: 12,
                                    border: '1px solid #e0e0e0'
                                }}>
                                    [IMAGE: describe what you want to see]
                                </div>
                                <Text variant="small" style={{ display: 'block', marginBottom: 8, color: '#666' }}>
                                    {isRtl ? '💡 דוגמאות:' : '💡 Examples:'}
                                </Text>
                                <ul style={{ margin: 0, paddingInlineStart: 20, fontSize: 12, color: '#666' }}>
                                    <li>[IMAGE: Modern server room with blue lighting]</li>
                                    <li>[IMAGE: Team collaboration in office meeting]</li>
                                    <li>[IMAGE: Cloud architecture diagram with icons]</li>
                                </ul>
                                <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                                    <DefaultButton
                                        text={imageCopied ? (isRtl ? '✓ הועתק!' : '✓ Copied!') : (isRtl ? 'העתק תבנית' : 'Copy Template')}
                                        iconProps={{ iconName: imageCopied ? 'CheckMark' : 'Copy' }}
                                        onClick={() => {
                                            navigator.clipboard.writeText('[IMAGE: describe what you want to see]');
                                            setImageCopied(true);
                                            setTimeout(() => setImageCopied(false), 2000);
                                        }}
                                        styles={{ root: { flex: 1 } }}
                                    />
                                </div>
                            </div>
                        </Callout>
                    )}
                    <TooltipHost content={isRtl ? "מחק" : "Delete"}>
                        <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        />
                    </TooltipHost>
                </div>
            </div>
            
            {isExpanded && (
                <div className={styles.sectionBody}>
                    {showAiInput && (
                        <div className={styles.aiInputContainer}>
                            <TextField
                                placeholder={isRtl ? "הוסף פרטים על אבטחה..." : "Expand with more security details..."}
                                value={aiInstruction}
                                onChange={(_, v) => setAiInstruction(v || '')}
                                onKeyPress={(e) => e.key === 'Enter' && handleAiSubmit()}
                                className={styles.aiInput}
                            />
                            <DefaultButton
                                text={isRtl ? "עדכן" : "Update"}
                                onClick={handleAiSubmit}
                                disabled={!aiInstruction.trim()}
                            />
                        </div>
                    )}
                    <div className={styles.editorWrapper}>
                        <EditorContent editor={editor} />
                    </div>
                </div>
            )}
        </div>
    );
};

// Main Panel Component
export const DocumentDraftPanel: React.FC<DocumentDraftPanelProps> = ({
    draft: initialDraft,
    conversationId,
    workspaceId,
    refreshKey = 0,
    isRtl = false,
    sidePanel = false,
    isCollapsed = false,
    isPinned = false,
    hasUpdate = false,
    onClose,
    onCollapse,
    onExpand,
    onClearUpdate,
    onPin,
    onUnpin,
    onSectionUpdate,
    onAiRequest,
    onExport,
    onDraftChange,
    onDeleted
}) => {
    const [draft, setDraft] = useState<DocumentDraft | null>(initialDraft);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    // Initialize export format from the draft's format - no need to select again
    const [exportFormat, setExportFormat] = useState<'pdf' | 'docx' | 'pptx'>(
        initialDraft?.format || 'docx'
    );
    // Initialize exportedUrl from draft's existing export (for re-download after refresh)
    const [exportedUrl, setExportedUrl] = useState<string | null>(initialDraft?.exportedUrl || null);
    const [exportedFilename, setExportedFilename] = useState<string | null>(null);
    // Option to generate AI images for PPTX exports
    const [generateImages, setGenerateImages] = useState<boolean>(true);
    // Track if download link should persist (don't auto-dismiss)
    const [persistDownloadLink, setPersistDownloadLink] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [newSectionTitle, setNewSectionTitle] = useState('');
    const [showAddSection, setShowAddSection] = useState(false);
    
    // Drag and drop state
    const [draggedSectionId, setDraggedSectionId] = useState<string | null>(null);
    const [dragOverSectionId, setDragOverSectionId] = useState<string | null>(null);
    
    // Sharing modal state
    const [showSharingModal, setShowSharingModal] = useState(false);
    
    // Full document AI input state
    const [fullDocInstruction, setFullDocInstruction] = useState('');
    const [isFullDocUpdating, setIsFullDocUpdating] = useState(false);
    const [aiProgressMessage, setAiProgressMessage] = useState<string>('');
    const [enableWebSearch, setEnableWebSearch] = useState(true);

    // Load or create draft
    useEffect(() => {
        const loadDraft = async () => {
            if (initialDraft) {
                setDraft(initialDraft);
                // Expand first section by default
                if (initialDraft.sections.length > 0) {
                    setExpandedSections(new Set([initialDraft.sections[0].id]));
                }
                return;
            }

            // Load by workspace ID if provided
            if (workspaceId) {
                // Only show loading spinner on initial load, not on refresh
                const isInitialLoad = !draft;
                if (isInitialLoad) {
                    setIsLoading(true);
                }
                try {
                    const loadedDraft = await getWorkspaceById(workspaceId);
                    setDraft(loadedDraft);
                    // Use the draft's format for export
                    if (loadedDraft?.format) {
                        setExportFormat(loadedDraft.format as 'pdf' | 'docx' | 'pptx');
                    }
                    if (loadedDraft?.sections.length) {
                        setExpandedSections(new Set([loadedDraft.sections[0].id]));
                    }
                } catch (e) {
                    setError('Failed to load workspace');
                } finally {
                    if (isInitialLoad) {
                        setIsLoading(false);
                    }
                }
                return;
            }

            if (!conversationId) return;

            setIsLoading(true);
            try {
                let loadedDraft = await getDraftByConversation(conversationId);
                if (!loadedDraft) {
                    loadedDraft = await createDraft(
                        conversationId,
                        'Untitled Document',
                        'docx'
                    );
                }
                setDraft(loadedDraft);
                // Use the draft's format for export
                if (loadedDraft?.format) {
                    setExportFormat(loadedDraft.format as 'pdf' | 'docx' | 'pptx');
                }
                if (loadedDraft?.sections.length) {
                    setExpandedSections(new Set([loadedDraft.sections[0].id]));
                }
            } catch (e) {
                setError('Failed to load document');
            } finally {
                setIsLoading(false);
            }
        };

        loadDraft();
    }, [initialDraft, conversationId, workspaceId, refreshKey]);

    // Notify parent of draft changes
    useEffect(() => {
        if (draft && onDraftChange) {
            onDraftChange(draft);
        }
    }, [draft, onDraftChange]);

    const toggleSection = useCallback((sectionId: string) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(sectionId)) {
                next.delete(sectionId);
            } else {
                next.add(sectionId);
            }
            return next;
        });
    }, []);

    const handleContentChange = useCallback(async (sectionId: string, content: string) => {
        if (!draft) return;
        
        // Update local state immediately
        setDraft(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                sections: prev.sections.map(s => 
                    s.id === sectionId ? { ...s, content } : s
                )
            };
        });
        
        // Debounced save to server
        await updateSection(draft.id, sectionId, content);
        onSectionUpdate?.(sectionId, content);
    }, [draft, onSectionUpdate]);

    const handleAiRequest = useCallback(async (sectionId: string, instruction: string) => {
        if (!draft) return;
        
        // Mark section as AI updating
        setDraft(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                sections: prev.sections.map(s => 
                    s.id === sectionId ? { ...s, status: 'ai_updating' as const } : s
                )
            };
        });
        
        const updatedSection = await requestAiUpdate(draft.id, sectionId, instruction);
        
        if (updatedSection) {
            setDraft(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    sections: prev.sections.map(s => 
                        s.id === sectionId ? updatedSection : s
                    )
                };
            });
        }
        
        onAiRequest?.(sectionId, instruction);
    }, [draft, onAiRequest]);

    // Full document AI update handler
    const handleFullDocumentUpdate = useCallback(async () => {
        if (!draft || !fullDocInstruction.trim()) return;
        
        setIsFullDocUpdating(true);
        setError(null);
        
        // Show progress messages
        const progressMessages = enableWebSearch
            ? [
                'Analyzing your request...',
                'Researching online sources...',
                'Gathering relevant information...',
                'Generating content...',
                'Finalizing document...'
            ]
            : [
                'Analyzing your request...',
                'Processing content...',
                'Generating document...',
                'Finalizing...'
            ];
        
        let messageIndex = 0;
        setAiProgressMessage(progressMessages[0]);
        
        const progressInterval = setInterval(() => {
            messageIndex = Math.min(messageIndex + 1, progressMessages.length - 1);
            setAiProgressMessage(progressMessages[messageIndex]);
        }, 2500);
        
        try {
            const updatedDraft = await requestFullDocumentUpdate(
                draft.id, 
                fullDocInstruction, 
                enableWebSearch
            );
            
            clearInterval(progressInterval);
            
            if (updatedDraft) {
                setDraft(updatedDraft);
                setFullDocInstruction('');
                // Expand first section
                if (updatedDraft.sections.length > 0) {
                    setExpandedSections(new Set([updatedDraft.sections[0].id]));
                }
                onDraftChange?.(updatedDraft);
            } else {
                setError('Failed to update document');
            }
        } catch (e) {
            clearInterval(progressInterval);
            setError('Error updating document');
        } finally {
            setIsFullDocUpdating(false);
            setAiProgressMessage('');
        }
    }, [draft, fullDocInstruction, enableWebSearch, onDraftChange]);

    const handleDeleteSection = useCallback(async (sectionId: string) => {
        if (!draft) return;
        
        const success = await deleteSection(draft.id, sectionId);
        if (success) {
            setDraft(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    sections: prev.sections.filter(s => s.id !== sectionId)
                };
            });
        }
    }, [draft]);

    const handleAddSection = useCallback(async () => {
        if (!draft || !newSectionTitle.trim()) return;
        
        const section = await addSection(draft.id, newSectionTitle, '');
        if (section) {
            setDraft(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    sections: [...prev.sections, section]
                };
            });
            setExpandedSections(prev => new Set([...prev, section.id]));
            setNewSectionTitle('');
            setShowAddSection(false);
        }
    }, [draft, newSectionTitle]);

    // Drag and drop handlers for section reordering
    const handleDragStart = useCallback((e: React.DragEvent, sectionId: string) => {
        setDraggedSectionId(sectionId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', sectionId);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent, sectionId: string) => {
        e.preventDefault();
        if (sectionId !== draggedSectionId) {
            setDragOverSectionId(sectionId);
        }
    }, [draggedSectionId]);

    const handleDragEnd = useCallback(() => {
        setDraggedSectionId(null);
        setDragOverSectionId(null);
    }, []);

    const handleDrop = useCallback(async (e: React.DragEvent, targetSectionId: string) => {
        e.preventDefault();
        if (!draft || !draggedSectionId || draggedSectionId === targetSectionId) {
            handleDragEnd();
            return;
        }

        // Reorder sections locally
        const sections = [...draft.sections].sort((a, b) => a.order - b.order);
        const draggedIndex = sections.findIndex(s => s.id === draggedSectionId);
        const targetIndex = sections.findIndex(s => s.id === targetSectionId);

        if (draggedIndex === -1 || targetIndex === -1) {
            handleDragEnd();
            return;
        }

        // Remove dragged item and insert at new position
        const [draggedSection] = sections.splice(draggedIndex, 1);
        sections.splice(targetIndex, 0, draggedSection);

        // Update order values
        const reorderedSections = sections.map((s, index) => ({
            ...s,
            order: index
        }));

        // Update local state immediately for responsiveness
        setDraft(prev => prev ? { ...prev, sections: reorderedSections } : prev);
        handleDragEnd();

        // Save to backend
        const sectionOrder = reorderedSections.map(s => s.id);
        const success = await reorderSections(draft.id, sectionOrder);
        
        if (!success) {
            setError(isRtl ? 'שגיאה בשינוי סדר הסעיפים' : 'Failed to reorder sections');
            // Reload draft to restore correct order
            if (workspaceId) {
                const reloadedDraft = await getWorkspaceById(workspaceId);
                if (reloadedDraft) setDraft(reloadedDraft);
            }
        }
    }, [draft, draggedSectionId, handleDragEnd, isRtl, workspaceId]);

    const handleExport = useCallback(async () => {
        if (!draft) return;
        
        setIsExporting(true);
        setError(null);
        // Don't clear exportedUrl - keep the download link available
        
        try {
            // Pass generateImages option for PPTX exports
            const shouldGenerateImages = exportFormat === 'pptx' ? generateImages : true;
            const result = await exportDraft(draft.id, exportFormat, shouldGenerateImages);
            if (result.success && result.document_url) {
                setExportedUrl(result.document_url);
                setExportedFilename(result.filename || `${draft.title}.${exportFormat}`);
                setPersistDownloadLink(true);  // Ensure link persists
                onExport?.(exportFormat);
            } else {
                setError(result.error || 'Export failed');
            }
        } catch (e) {
            setError('Export failed');
        } finally {
            setIsExporting(false);
        }
    }, [draft, exportFormat, generateImages, onExport]);

    const handleDeleteWorkspace = useCallback(async () => {
        if (!draft) return;
        
        setIsDeleting(true);
        try {
            const success = await deleteWorkspace(draft.id);
            if (success) {
                setShowDeleteConfirm(false);
                onDeleted?.();
                onClose();
            } else {
                setError('Failed to delete workspace');
            }
        } catch (e) {
            setError('Error deleting workspace');
        } finally {
            setIsDeleting(false);
        }
    }, [draft, onDeleted, onClose]);

    // Collapsed panel view - minimal strip with expand button at top
    if (isCollapsed && sidePanel) {
        const handleExpand = () => {
            onExpand?.();
            onClearUpdate?.();  // Clear update indicator when expanding
        };
        return (
            <div className={`${styles.collapsedPanel} ${isRtl ? styles.rtl : ''} ${hasUpdate ? styles.hasUpdate : ''}`}>
                <IconButton
                    iconProps={{ iconName: isRtl ? 'ChevronRight' : 'ChevronLeft' }}
                    onClick={handleExpand}
                    title={isRtl ? 'הרחב' : 'Expand'}
                    className={styles.expandButton}
                />
                <div className={styles.collapsedIcon}>
                    <Icon iconName="TextDocument" />
                </div>
                <TooltipHost content={draft?.title || 'Document'}>
                    <Text className={styles.collapsedTitle}>
                        {draft?.title ? (draft.title.length > 12 ? draft.title.slice(0, 12) + '...' : draft.title) : 'Doc'}
                    </Text>
                </TooltipHost>
                {isPinned && (
                    <span className={styles.pinnedBadge} title="Pinned">📌</span>
                )}
                {hasUpdate && (
                    <span className={styles.updateBadge} title={isRtl ? 'תוכן חדש נוסף' : 'New content added'}>✓</span>
                )}
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className={sidePanel ? styles.sidePanelContainer : styles.overlay}>
                <div className={sidePanel ? styles.sidePanel : styles.panel}>
                    <div className={styles.loadingContainer}>
                        <Spinner size={SpinnerSize.large} />
                        <Text>{isRtl ? 'טוען מסמך...' : 'Loading document...'}</Text>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={sidePanel ? styles.sidePanelContainer : styles.overlay}>
            <div className={`${sidePanel ? styles.sidePanel : styles.panel} ${isRtl ? styles.rtl : ''}`}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <IconButton
                            iconProps={{ iconName: 'ChromeClose' }}
                            onClick={onClose}
                            className={styles.closeButton}
                            title={isPinned ? (isRtl ? "בטל הצמדה וסגור" : "Unpin and close") : (isRtl ? "סגור" : "Close")}
                        />
                        <Text className={styles.title}>{draft?.title || 'Document'}</Text>
                    </div>
                    <div className={styles.headerRight}>
                        {/* Pin/Unpin button */}
                        {sidePanel && (
                            <TooltipHost content={isPinned 
                                ? (isRtl ? "בטל הצמדה - המסמך ייסגר בסיום השיחה" : "Unpin - document will close when leaving conversation") 
                                : (isRtl ? "הצמד - המסמך יישאר פתוח למחקר" : "Pin - keep document open for research")}>
                                <IconButton
                                    iconProps={{ iconName: isPinned ? 'Pinned' : 'Pin' }}
                                    onClick={isPinned ? onUnpin : onPin}
                                    styles={{
                                        root: {
                                            color: isPinned ? '#667eea' : '#64748b',
                                            backgroundColor: isPinned ? 'rgba(102, 126, 234, 0.12)' : 'transparent',
                                            marginRight: 4
                                        },
                                        rootHovered: {
                                            color: '#667eea',
                                            backgroundColor: 'rgba(102, 126, 234, 0.15)'
                                        }
                                    }}
                                />
                            </TooltipHost>
                        )}
                        {/* Collapse button (side panel only) */}
                        {sidePanel && onCollapse && (
                            <TooltipHost content={isRtl ? "כווץ" : "Collapse"}>
                                <IconButton
                                    iconProps={{ iconName: 'ChevronRight' }}
                                    onClick={onCollapse}
                                    styles={{
                                        root: { marginRight: 8 }
                                    }}
                                />
                            </TooltipHost>
                        )}
                        {/* Share button */}
                        <TooltipHost content={isRtl ? "שתף מסמך" : "Share document"}>
                            <IconButton
                                iconProps={{ iconName: 'Share' }}
                                onClick={() => setShowSharingModal(true)}
                                styles={{
                                    root: {
                                        color: '#0078d4',
                                        marginRight: 8
                                    },
                                    rootHovered: {
                                        color: '#106ebe',
                                        backgroundColor: '#e5f1fb'
                                    }
                                }}
                            />
                        </TooltipHost>
                        <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            title="Delete workspace"
                            onClick={() => setShowDeleteConfirm(true)}
                            styles={{
                                root: {
                                    color: '#d13438',
                                    marginRight: 8
                                },
                                rootHovered: {
                                    color: '#a4262c',
                                    backgroundColor: '#fde7e9'
                                }
                            }}
                        />
                        {/* Export format selector */}
                        <Dropdown
                            selectedKey={exportFormat}
                            options={[
                                { key: 'docx', text: 'Word (.docx)' },
                                { key: 'pdf', text: 'PDF' },
                                { key: 'pptx', text: 'PowerPoint (.pptx)' }
                            ]}
                            onChange={(_, option) => option && setExportFormat(option.key as 'pdf' | 'docx' | 'pptx')}
                            styles={{
                                root: { minWidth: 130, marginRight: 8 },
                                dropdown: { borderRadius: 6 },
                                title: { borderRadius: 6, fontSize: 13 },
                                dropdownItem: { fontSize: 13 }
                            }}
                            disabled={isExporting}
                        />
                        {/* AI Images toggle for PowerPoint */}
                        {exportFormat === 'pptx' && (
                            <TooltipHost content={isRtl ? 'יצירת תמונות AI לשקפים' : 'Generate AI images for slides'}>
                                <label style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    marginRight: 12,
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    color: '#666'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={generateImages}
                                        onChange={(e) => setGenerateImages(e.target.checked)}
                                        disabled={isExporting}
                                        style={{ marginRight: 4 }}
                                    />
                                    <Icon iconName="Photo2" style={{ marginRight: 4, fontSize: 14 }} />
                                    {isRtl ? 'תמונות' : 'Images'}
                                </label>
                            </TooltipHost>
                        )}
                        <PrimaryButton
                            text={isRtl ? 'ייצוא' : 'Export'}
                            iconProps={{ iconName: 'Download' }}
                            onClick={handleExport}
                            disabled={isExporting || !draft?.sections.length}
                        />
                    </div>
                </div>

                {/* Collaborators Bar - shows shared users */}
                {draft?.sharedWithUsers && draft.sharedWithUsers.length > 0 && (
                    <div style={{
                        padding: '8px 16px',
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #e1e1e1',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <Icon iconName="People" style={{ color: '#666', fontSize: '14px' }} />
                        <Text style={{ fontSize: '12px', color: '#666', marginRight: '8px' }}>
                            {isRtl ? 'משתפים:' : 'Collaborators:'}
                        </Text>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                            {draft.sharedWithUsers.slice(0, 5).map((email, index) => {
                                const initials = email.split('@')[0].substring(0, 2).toUpperCase();
                                const colors = ['#0078d4', '#107c10', '#5c2d91', '#ca5010', '#d13438'];
                                const bgColor = colors[index % colors.length];
                                
                                return (
                                    <TooltipHost key={email} content={email}>
                                        <div style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '50%',
                                            backgroundColor: bgColor,
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            border: '2px solid white',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                                        }}>
                                            {initials}
                                        </div>
                                    </TooltipHost>
                                );
                            })}
                            {draft.sharedWithUsers.length > 5 && (
                                <TooltipHost content={draft.sharedWithUsers.slice(5).join(', ')}>
                                    <div style={{
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '50%',
                                        backgroundColor: '#888',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        border: '2px solid white'
                                    }}>
                                        +{draft.sharedWithUsers.length - 5}
                                    </div>
                                </TooltipHost>
                            )}
                        </div>
                        {draft.isPublic && (
                            <TooltipHost content={isRtl ? 'מסמך ציבורי לצפייה' : 'Public document (view-only)'}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    marginLeft: '8px',
                                    padding: '2px 8px',
                                    backgroundColor: '#e3f2fd',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    color: '#0078d4'
                                }}>
                                    <Icon iconName="Globe" style={{ fontSize: '12px' }} />
                                    {isRtl ? 'ציבורי' : 'Public'}
                                </div>
                            </TooltipHost>
                        )}
                        <IconButton
                            iconProps={{ iconName: 'Settings' }}
                            onClick={() => setShowSharingModal(true)}
                            styles={{
                                root: { 
                                    marginLeft: 'auto', 
                                    width: 24, 
                                    height: 24,
                                    color: '#666'
                                },
                                icon: { fontSize: 12 }
                            }}
                            title={isRtl ? 'נהל שיתוף' : 'Manage sharing'}
                        />
                    </div>
                )}

                {/* Delete Confirmation Dialog */}
                {showDeleteConfirm && (
                    <MessageBar
                        messageBarType={MessageBarType.severeWarning}
                        onDismiss={() => setShowDeleteConfirm(false)}
                        actions={
                            <Stack horizontal tokens={{ childrenGap: 8 }}>
                                <DefaultButton
                                    text={isDeleting ? 'Deleting...' : 'Delete'}
                                    onClick={handleDeleteWorkspace}
                                    disabled={isDeleting}
                                    styles={{
                                        root: { backgroundColor: '#d13438', color: 'white', border: 'none' },
                                        rootHovered: { backgroundColor: '#a4262c', color: 'white' }
                                    }}
                                />
                                <DefaultButton
                                    text="Cancel"
                                    onClick={() => setShowDeleteConfirm(false)}
                                    disabled={isDeleting}
                                />
                            </Stack>
                        }
                    >
                        Are you sure you want to delete "{draft?.title}"? This cannot be undone.
                    </MessageBar>
                )}

                {/* Export Progress Indicator */}
                {isExporting && (
                    <MessageBar messageBarType={MessageBarType.info}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                            <Spinner size={SpinnerSize.small} />
                            <Text>
                                {isRtl 
                                    ? 'מייצא מסמך... אם יש תמונות, זה עשוי לקחת דקה.' 
                                    : 'Exporting document... If there are images, this may take a minute.'}
                            </Text>
                        </Stack>
                    </MessageBar>
                )}

                {/* Error message */}
                {error && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        onDismiss={() => setError(null)}
                    >
                        {error}
                    </MessageBar>
                )}

                {/* Export Success - Download Link (keeps visible until new export or manual dismiss) */}
                {exportedUrl && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 16px',
                        backgroundColor: '#dff6dd',
                        borderBottom: '1px solid #c6e8c3',
                        gap: 12
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Icon iconName="CheckMark" style={{ color: '#107c10', fontSize: 16 }} />
                            <Text style={{ color: '#107c10', fontWeight: 500 }}>
                                {isRtl 
                                    ? `המסמך "${exportedFilename || 'document'}" מוכן!` 
                                    : `Document "${exportedFilename || 'document'}" is ready!`}
                            </Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {/* View button - opens in Office 365 for Office files, inline for PDFs */}
                            <button
                                onClick={() => openFile(exportedUrl, exportedFilename || 'document')}
                                style={{ 
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '6px 12px',
                                    backgroundColor: '#0078d4',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    fontSize: 13
                                }}
                            >
                                <Icon iconName="View" style={{ fontSize: 14 }} />
                                {isRtl ? 'צפייה' : 'View'}
                            </button>
                            {/* Download button - always downloads */}
                            <button
                                onClick={() => downloadFile(exportedUrl, exportedFilename || 'document')}
                                style={{ 
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '6px 12px',
                                    backgroundColor: '#107c10',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    fontSize: 13
                                }}
                            >
                                <Icon iconName="Download" style={{ fontSize: 14 }} />
                                {isRtl ? 'הורדה' : 'Download'}
                            </button>
                            <IconButton
                                iconProps={{ iconName: 'Cancel' }}
                                title={isRtl ? 'סגור' : 'Dismiss'}
                                onClick={() => setExportedUrl(null)}
                                styles={{
                                    root: { width: 24, height: 24 },
                                    icon: { fontSize: 12, color: '#666' }
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Full Document AI Command Bar */}
                <div className={styles.fullDocCommandBar}>
                    <div className={styles.commandInputRow}>
                        <TextField
                            placeholder="Update entire document: e.g., 'Add a literature review section with real research examples' or 'Enrich all sections with statistics'"
                            value={fullDocInstruction}
                            onChange={(_, v) => setFullDocInstruction(v || '')}
                            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleFullDocumentUpdate()}
                            disabled={isFullDocUpdating}
                            multiline={false}
                            styles={{
                                root: { flex: 1 },
                                fieldGroup: { 
                                    borderRadius: '4px',
                                    border: '1px solid #e0e0e0'
                                }
                            }}
                        />
                        <TooltipHost content={enableWebSearch ? "Web search enabled - will research online" : "Web search disabled - model knowledge only"}>
                            <IconButton
                                iconProps={{ iconName: enableWebSearch ? 'Globe' : 'GlobeFavorite' }}
                                onClick={() => setEnableWebSearch(!enableWebSearch)}
                                styles={{
                                    root: {
                                        backgroundColor: enableWebSearch ? '#e3f2fd' : 'transparent',
                                        borderRadius: '4px'
                                    }
                                }}
                                title={enableWebSearch ? "Web Search: ON" : "Web Search: OFF"}
                            />
                        </TooltipHost>
                        <PrimaryButton
                            text={isFullDocUpdating ? 'Updating...' : 'Update Document'}
                            iconProps={{ iconName: 'Lightbulb' }}
                            onClick={handleFullDocumentUpdate}
                            disabled={!fullDocInstruction.trim() || isFullDocUpdating}
                        />
                    </div>
                    <Text variant="tiny" styles={{ root: { color: '#666', marginTop: 4 } }}>
                        This updates the entire document. For section-specific changes, use the section input below.
                        {enableWebSearch && ' 🔍 Web research enabled for real-time data.'}
                    </Text>
                </div>

                {/* AI Working Progress Indicator */}
                {isFullDocUpdating && aiProgressMessage && (
                    <div className={styles.aiWorkingOverlay}>
                        <div className={styles.aiWorkingContent}>
                            <Spinner size={SpinnerSize.medium} />
                            <div className={styles.aiWorkingText}>
                                <Text className={styles.aiWorkingTitle}>
                                    {isRtl ? 'עובד על המסמך שלך...' : 'Working on your document...'}
                                </Text>
                                <Text className={styles.aiWorkingMessage}>
                                    {aiProgressMessage}
                                </Text>
                            </div>
                        </div>
                    </div>
                )}

                {/* Sections */}
                <div className={styles.sectionsContainer}>
                    {draft?.sections.sort((a, b) => a.order - b.order).map(section => (
                        <SectionEditor
                            key={section.id}
                            section={section}
                            isExpanded={expandedSections.has(section.id)}
                            onToggle={() => toggleSection(section.id)}
                            onContentChange={(content) => handleContentChange(section.id, content)}
                            onAiRequest={(instruction) => handleAiRequest(section.id, instruction)}
                            onDelete={() => handleDeleteSection(section.id)}
                            isRtl={isRtl}
                            onDragStart={handleDragStart}
                            onDragOver={(e) => handleDragOver(e, section.id)}
                            onDragEnd={handleDragEnd}
                            onDrop={handleDrop}
                            isDragging={draggedSectionId === section.id}
                            isDragOver={dragOverSectionId === section.id}
                        />
                    ))}

                    {/* Add Section */}
                    {showAddSection ? (
                        <div className={styles.addSectionForm}>
                            <TextField
                                placeholder={isRtl ? "שם הסעיף החדש" : "New section title"}
                                value={newSectionTitle}
                                onChange={(_, v) => setNewSectionTitle(v || '')}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddSection()}
                            />
                            <Stack horizontal tokens={{ childrenGap: 8 }}>
                                <DefaultButton
                                    text={isRtl ? "הוסף" : "Add"}
                                    onClick={handleAddSection}
                                    disabled={!newSectionTitle.trim()}
                                />
                                <DefaultButton
                                    text={isRtl ? "ביטול" : "Cancel"}
                                    onClick={() => setShowAddSection(false)}
                                />
                            </Stack>
                        </div>
                    ) : (
                        <DefaultButton
                            text={isRtl ? "+ הוסף סעיף" : "+ Add Section"}
                            onClick={() => setShowAddSection(true)}
                            className={styles.addSectionButton}
                        />
                    )}
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <Text className={styles.versionText}>
                        {isRtl ? `גרסה ${draft?.version || 1}` : `Version ${draft?.version || 1}`}
                    </Text>
                    <Text className={styles.sectionCount}>
                        {isRtl 
                            ? `${draft?.sections.length || 0} סעיפים` 
                            : `${draft?.sections.length || 0} sections`}
                    </Text>
                </div>
            </div>
            
            {/* Sharing Modal */}
            {draft && (
                <DocumentWorkspaceSharingModal
                    workspace={draft as any}
                    isOpen={showSharingModal}
                    onClose={() => setShowSharingModal(false)}
                    onUpdated={(updatedWorkspace) => {
                        // Update draft state with new sharing info so UI reflects changes immediately
                        setDraft(prev => prev ? {
                            ...prev,
                            sharedWithUsers: updatedWorkspace.sharedWithUsers,
                            sharedWithGroups: updatedWorkspace.sharedWithGroups,
                            isPublic: updatedWorkspace.isPublic
                        } : prev);
                        setShowSharingModal(false);
                    }}
                />
            )}
        </div>
    );
};

export default DocumentDraftPanel;
